/**
 * How far through a session's roster the teacher is — the "1/3 đã điểm" line
 * and the 2px bar under it on the workbench card and session detail (P2.3 §1).
 *
 * Pure data-in/data-out — no React, no i18n — so the two surfaces read one
 * rule instead of each counting for itself.
 *
 * "Marked" is the same predicate the shipped surfaces already use for
 * `allTaken` (TodaySessionCard) and the week strip's day state
 * (lib/attendanceStatus.ts): anything that is not PENDING. There is no fourth
 * status to worry about.
 */
interface ProgressParticipant {
  /** The status to count — see the note on `attendanceProgress` about which. */
  attendance: string;
}

export interface AttendanceProgress {
  marked: number;
  total: number;
  /** 0–1, for the bar's width. 0 on an empty roster (no divide-by-zero). */
  ratio: number;
}

/**
 * Callers pass the **effective** mark — `marks[p.enrollmentId] ?? p.attendance`
 * — not the saved one. The bar is feedback on the tap that just happened; a
 * teacher ticking three students and watching "0/3 đã điểm" sit still would
 * reasonably conclude the taps did nothing. Nothing reads as falsely saved,
 * because reaching 3/3 by tapping is exactly when the sticky footer appears
 * saying "Lưu điểm danh".
 */
export function attendanceProgress(participants: ProgressParticipant[]): AttendanceProgress {
  const total = participants.length;
  const marked = participants.filter((p) => p.attendance !== 'PENDING').length;
  return { marked, total, ratio: total === 0 ? 0 : marked / total };
}
